/**
 * Renders the streamed answer, turning inline citation markers — `[S1]`, `[E2]`, or a combined
 * `[S1, S3]` — into chips that link to the citation's `href`. A marker the model wrote but that
 * never resolved to a real citation (the loop drops hallucinated handles before it ever reaches
 * `done.citations`, see api/src/wow/ask/citations.ts) renders as plain text, never a broken link.
 */
import { Link } from 'react-router-dom';
import { FileText } from 'lucide-react';
import type { Citation } from './types';

/** Identical to the server's marker pattern (api/src/wow/ask/citations.ts) — one or more of
 * `S1`/`E2` separated by commas, inside a single pair of brackets. */
export const CITATION_MARKER_RE = /\[((?:[SE]\d+)(?:\s*,\s*[SE]\d+)*)\]/g;

export type AnswerSegment =
  | { kind: 'text'; text: string }
  | { kind: 'citation'; handle: string; citation: Citation };

/**
 * Pure: splits answer prose into text and citation segments. Exported so it can be unit-tested
 * without rendering — see Answer.test.ts.
 */
export function splitAnswerSegments(text: string, citations: Citation[]): AnswerSegment[] {
  const byHandle = new Map(citations.map((citation) => [citation.handle, citation] as const));
  const segments: AnswerSegment[] = [];

  const pushText = (value: string) => {
    if (!value) return;
    const last = segments[segments.length - 1];
    if (last && last.kind === 'text') last.text += value;
    else segments.push({ kind: 'text', text: value });
  };

  const re = new RegExp(CITATION_MARKER_RE.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) pushText(text.slice(lastIndex, match.index));
    for (const rawHandle of (match[1] ?? '').split(',')) {
      const handle = rawHandle.trim();
      const citation = byHandle.get(handle);
      if (citation) segments.push({ kind: 'citation', handle, citation });
      else pushText(`[${handle}]`);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) pushText(text.slice(lastIndex));
  return segments;
}

export function Answer({ text, citations }: { text: string; citations: Citation[] }) {
  if (!text) return null;
  const segments = splitAnswerSegments(text, citations);
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
      {segments.map((segment, i) =>
        segment.kind === 'text' ? (
          <span key={i}>{segment.text}</span>
        ) : (
          <CitationChip key={i} citation={segment.citation} />
        ),
      )}
    </p>
  );
}

function CitationChip({ citation }: { citation: Citation }) {
  if (citation.kind === 'section') {
    return (
      <Link
        to={citation.href}
        title={`${citation.documentTitle} — ${citation.headingPath}`}
        className="mx-0.5 inline-flex max-w-[14rem] items-center gap-1 rounded-full border border-border bg-secondary px-1.5 py-0.5 align-baseline text-[11px] font-medium leading-4 text-secondary-foreground transition-colors duration-150 hover:bg-muted"
      >
        <FileText className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate">{citation.headingPath}</span>
      </Link>
    );
  }
  return (
    <Link
      to={citation.href}
      title={`${citation.entityType} — ${citation.name}`}
      className="mx-0.5 inline-flex items-center gap-1 rounded-full border border-border bg-secondary px-1.5 py-0.5 align-baseline text-[11px] font-medium leading-4 text-secondary-foreground transition-colors duration-150 hover:bg-muted"
    >
      <span className="font-mono">{citation.entityKey}</span>
    </Link>
  );
}
